import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Fastest Laps Widget
 * Per-race fastest lap leaderboard with speed comparisons
 * Shows driver, team, lap time, and speed data
 */
const F1FastestLaps = ({ season = 2025, raceId = null }) => {
  const [fastestLaps, setFastestLaps] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState(raceId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  // Fetch available races
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const response = await f1Service.getCompletedRaces(season);
        if (response && response.response) {
          setRaces(response.response);
          // Auto-select most recent race if no race selected
          if (!selectedRaceId && response.response.length > 0) {
            setSelectedRaceId(response.response[response.response.length - 1].id);
          }
        }
      } catch (err) {
        console.error('Error fetching races:', err);
      }
    };

    fetchRaces();
  }, [season, selectedRaceId]);

  // Fetch fastest laps for selected race
  useEffect(() => {
    if (!selectedRaceId) {
      setLoading(false);
      return;
    }

    const fetchFastestLaps = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await f1Service.getFastestLaps(selectedRaceId);

        if (response && response.response) {
          // Sort by lap time (fastest first)
          const sorted = response.response.sort((a, b) => {
            const timeA = parseTime(a.time);
            const timeB = parseTime(b.time);
            return timeA - timeB;
          });
          setFastestLaps(sorted);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching fastest laps:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchFastestLaps();
  }, [selectedRaceId]);

  // Parse lap time string (e.g., "1:23.456") to milliseconds
  const parseTime = (timeString) => {
    if (!timeString) return Infinity;
    try {
      const parts = timeString.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]);
        const seconds = parseFloat(parts[1]);
        return (minutes * 60 + seconds) * 1000;
      }
      return parseFloat(timeString) * 1000;
    } catch (e) {
      return Infinity;
    }
  };

  // Get team color for driver
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Calculate time delta from fastest
  const getTimeDelta = (time, fastestTime) => {
    if (!time || !fastestTime) return null;
    const delta = parseTime(time) - parseTime(fastestTime);
    if (delta === 0) return null;
    return `+${(delta / 1000).toFixed(3)}`;
  };

  // Get selected race info
  const selectedRace = races.find(r => r.id === selectedRaceId);

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-2">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-3 w-8 bg-white/10 rounded animate-pulse" />
              <div className="h-3 flex-1 bg-white/10 rounded animate-pulse" />
              <div className="h-3 w-20 bg-white/10 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (fastestLaps.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Fastest Laps
          </h3>
        </div>
        <div className="p-8 text-center">
          <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
            {races.length === 0 ? 'No Completed Races Available' : 'No Fastest Lap Data Available'}
          </p>
        </div>
      </div>
    );
  }

  const fastestTime = fastestLaps[0]?.time;

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Fastest Laps
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            {season}
          </span>
        </div>

        {/* Race Selector */}
        {races.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider whitespace-nowrap">
              Race:
            </span>
            <div className="flex gap-2">
              {races.map((race) => (
                <button
                  key={race.id}
                  onClick={() => setSelectedRaceId(race.id)}
                  className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-all duration-150 border whitespace-nowrap ${
                    selectedRaceId === race.id
                      ? 'bg-white/10 text-white border-white/20'
                      : 'bg-transparent text-gray-600 border-white/5 hover:border-white/10'
                  }`}
                >
                  {race.competition?.location?.round || 'R' + (races.indexOf(race) + 1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected Race Info */}
        {selectedRace && (
          <div className="mt-2 text-xs text-gray-400 font-mono">
            {selectedRace.competition?.name || 'Grand Prix'}
          </div>
        )}
      </div>

      {/* Fastest Laps Table - Terminal Style */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          {/* Table Header */}
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">P</th>
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Driver</th>
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Team</th>
              <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Time</th>
              <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Δ</th>
              <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Lap</th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {fastestLaps.map((lap, index) => {
              const teamColor = getTeamColor(lap.team?.name);
              const isFastest = index === 0;
              const delta = getTimeDelta(lap.time, fastestTime);

              return (
                <motion.tr
                  key={lap.driver?.id || index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02, duration: 0.2 }}
                  className={`border-b border-white/5 transition-colors duration-150 ${
                    isFastest ? 'bg-purple-500/10' : 'hover:bg-white/[0.02]'
                  }`}
                >
                  {/* Position */}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-bold tabular-nums ${
                          isFastest ? 'text-purple-400' : 'text-gray-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                      {isFastest && (
                        <div className="w-1 h-4 bg-purple-500 rounded-full" />
                      )}
                    </div>
                  </td>

                  {/* Driver Name */}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {/* Team Color Bar */}
                      <div
                        className="w-0.5 h-5 rounded-full"
                        style={{ backgroundColor: teamColor }}
                      />
                      {/* Driver Info */}
                      <div className="text-white font-semibold">
                        {lap.driver?.abbr || lap.driver?.name}
                      </div>
                    </div>
                  </td>

                  {/* Team */}
                  <td className="p-3">
                    <div
                      className="text-gray-400 truncate max-w-[120px]"
                      style={{ color: `${teamColor}cc` }}
                    >
                      {f1Service.getTeamCode(lap.team?.name)}
                    </div>
                  </td>

                  {/* Lap Time */}
                  <td className="p-3 text-right">
                    <span className={`font-bold tabular-nums ${isFastest ? 'text-purple-400' : 'text-white'}`}>
                      {lap.time}
                    </span>
                  </td>

                  {/* Time Delta */}
                  <td className="p-3 text-right">
                    {delta ? (
                      <span className="text-gray-500 tabular-nums text-[11px]">
                        {delta}
                      </span>
                    ) : (
                      <span className="text-gray-700 text-[10px]">—</span>
                    )}
                  </td>

                  {/* Lap Number */}
                  <td className="p-3 text-right">
                    <span className="text-gray-400 tabular-nums">
                      {lap.lap || '—'}
                    </span>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <div className="flex items-center gap-4">
            <span>Total Laps Recorded</span>
            <span className="text-white font-bold">{fastestLaps.length}</span>
          </div>
          {fastestTime && (
            <div className="flex items-center gap-2">
              <span>Fastest</span>
              <span className="text-purple-400 font-bold">{fastestTime}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default F1FastestLaps;
